import { theme } from '@acorns/web-components'

import { GiftType, getImagePath } from '../../helpers'

type ShapeObject = {
  shape: string
  isFlipped: boolean
  color: string
}

export type GiftShapeType = {
  shape: string
  isFlipped: boolean
  color: string
  messageDisplayed: boolean
  isClockwise: boolean
  squirrelIcon: string
  rotationStartAngle: string
  rotationEndAngle: string
} & GiftType

export const isRowStart = (index: number): boolean => [0, 4, 8].includes(index)

export const isRowEnd = (index: number): boolean => [3, 7, 11].includes(index)

export const isColumnStart = (index: number): boolean =>
  [0, 1, 2, 3].includes(index)

export const isColumnEnd = (index: number): boolean =>
  [9, 10, 11, 12].includes(index)

export const SHAPE_COLOR_MAP = [
  theme.colors.lightOak,
  theme.colors.bark,
  theme.colors.beige,
  theme.colors.dusk,
]

export const squirrelColorMap = [...SHAPE_COLOR_MAP].filter(
  (color) => color !== theme.colors.dusk,
)

export const shapeMap = ['rectangle', 'circle', 'triangle']

export const selectShape = (
  xCoordinate: number,
  yCoordinate: number,
): ShapeObject => {
  const initialShapeMatrix = [
    {
      shape: shapeMap[0],
      isFlipped: false,
      color: SHAPE_COLOR_MAP[3],
    },
    {
      shape: shapeMap[2],
      isFlipped: true,
      color: SHAPE_COLOR_MAP[0],
    },
    {
      shape: shapeMap[2],
      isFlipped: false,
      color: SHAPE_COLOR_MAP[1],
    },
    {
      shape: shapeMap[1],
      isFlipped: false,
      color: SHAPE_COLOR_MAP[0],
    },
    {
      shape: shapeMap[2],
      isFlipped: false,
      color: SHAPE_COLOR_MAP[2],
    },
    {
      shape: shapeMap[0],
      isFlipped: true,
      color: SHAPE_COLOR_MAP[1],
    },
    {
      shape: shapeMap[0],
      isFlipped: false,
      color: SHAPE_COLOR_MAP[3],
    },
    {
      shape: shapeMap[2],
      isFlipped: true,
      color: SHAPE_COLOR_MAP[2],
    },
    {
      shape: shapeMap[0],
      isFlipped: false,
      color: SHAPE_COLOR_MAP[0],
    },
    {
      shape: shapeMap[1],
      isFlipped: false,
      color: SHAPE_COLOR_MAP[1],
    },
    {
      shape: shapeMap[2],
      isFlipped: true,
      color: SHAPE_COLOR_MAP[0],
    },
    {
      shape: shapeMap[2],
      isFlipped: false,
      color: SHAPE_COLOR_MAP[1],
    },
  ]

  return initialShapeMatrix[yCoordinate * 4 + xCoordinate]
}

export const addGiftsToMatrix = (
  giftMatrix: GiftShapeType[],
  gifts: GiftType[],
): GiftShapeType[] => {
  const matrixWithGifts = giftMatrix
  // Gift Squirrel Icons will only appear in these positions in this order
  // This order is based on design requirements
  const giftOrder = [
    {
      position: 6,
      color: SHAPE_COLOR_MAP[0],
      isClockwise: false,
      squirrelIcon: getImagePath('gift-squirrel-4'),
      rotationStartAngle: '15deg',
      rotationEndAngle: '-345deg',
    },
    {
      position: 1,
      color: SHAPE_COLOR_MAP[2],
      isClockwise: false,
      squirrelIcon: getImagePath('gift-squirrel-1'),
      rotationStartAngle: '-30deg',
      rotationEndAngle: '-390deg',
    },
    {
      position: 9,
      color: SHAPE_COLOR_MAP[2],
      isClockwise: false,
      squirrelIcon: getImagePath('gift-squirrel-5'),
      rotationStartAngle: '0deg',
      rotationEndAngle: '-360deg',
    },
    {
      position: 4,
      color: SHAPE_COLOR_MAP[1],
      isClockwise: true,
      squirrelIcon: getImagePath('gift-squirrel-3'),
      rotationStartAngle: '30deg',
      rotationEndAngle: '390deg',
    },
    {
      position: 3,
      color: SHAPE_COLOR_MAP[1],
      isClockwise: true,
      squirrelIcon: getImagePath('gift-squirrel-2'),
      rotationStartAngle: '30deg',
      rotationEndAngle: '390deg',
    },
    {
      position: 11,
      color: SHAPE_COLOR_MAP[2],
      isClockwise: true,
      squirrelIcon: getImagePath('gift-squirrel-6'),
      rotationStartAngle: '-15deg',
      rotationEndAngle: '345deg',
    },
  ]
  gifts.forEach((gift, index) => {
    const update = {
      note: gift.note,
      contributorName: gift.contributorName,
      color: giftOrder[index].color,
      isClockwise: giftOrder[index].isClockwise,
      squirrelIcon: giftOrder[index].squirrelIcon,
      rotationStartAngle: giftOrder[index].rotationStartAngle,
      rotationEndAngle: giftOrder[index].rotationEndAngle,
    }
    matrixWithGifts[giftOrder[index].position] = {
      ...matrixWithGifts[giftOrder[index].position],
      ...update,
    }
  })
  return matrixWithGifts
}

export const createGiftMatrix = (gifts: GiftType[]): GiftShapeType[] => {
  const tempGiftMatrix: GiftShapeType[][] = []
  const giftQueue = gifts

  for (let i = 0; i < 3; i++) {
    const innerRow: GiftShapeType[] = []
    for (let j = 0; j < 4; j++) {
      const renderGift = {
        ...selectShape(j, i),
        note: '',
        contributorName: '',
        messageDisplayed: false,
        isClockwise: true,
        squirrelIcon: '',
        rotationStartAngle: '0deg',
        rotationEndAngle: '360deg',
      }
      innerRow.push(renderGift)
    }
    tempGiftMatrix.push(innerRow)
  }
  const flattenedMatrix: GiftShapeType[] = []
  for (let i = 0; i < tempGiftMatrix.length; i++) {
    for (let j = 0; j < tempGiftMatrix[i].length; j++) {
      flattenedMatrix.push(tempGiftMatrix[i][j])
    }
  }
  const flattenedGiftMatrix: GiftShapeType[] = addGiftsToMatrix(
    flattenedMatrix,
    giftQueue,
  )
  return flattenedGiftMatrix
}

export const getRandomShape = (): ShapeObject => {
  return {
    shape: shapeMap[Math.floor(Math.random() * 3)],
    isFlipped: Math.random() > 0.5 ? true : false,
    color: SHAPE_COLOR_MAP[Math.floor(Math.random() * 4)],
  }
}

// assist showing graphic in a wave order from top-left to bottom right
export const getLoadingGroup = (index: number): number =>
  (index % 4) + Math.floor(index / 4)
